import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService, type LoginRequest, type RegisterRequest, type AuthResponse } from '../lib/api/auth'
import { resetAuthInterceptorState } from '../lib/api/client'
import { disconnectWebSocket } from '../lib/websocket/boardWebSocket'

// Import stores that need to be cleared on logout or user change
import { useJournalStore } from './journalStore'
import { useUserChannelsStore } from './userChannelsStore'
import { useBoardStore } from './boardStore'
import { useOnboardingStore } from './onboardingStore'


interface User {
  id: string
  email: string
  name?: string
  firstName?: string
  lastName?: string
  currency?: string
  timezone?: string
  isActive: boolean
  country?: string
  emailVerified?: boolean
  emailVerifiedAt?: string
  onboardingStatus?: string
  createdAt: string
}


interface Tokens {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

interface AuthState {
  user: User | null;
  tokens: Tokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Auth actions
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  socialLogin: (provider: string, token: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  refreshTokens: () => Promise<Tokens>;
  setTokens: (tokens: Tokens) => void;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null })
        resetAuthInterceptorState()
        try {
          const response: AuthResponse = await authService.login(credentials)
          if (response.success) {
            const { user, tokens } = response.data

            // Check if user ID has changed (different user or same email after db reset)
            // If so, clear all stores to prevent stale data from previous user
            const previousUserId = get().user?.id;
            if (previousUserId && previousUserId !== user.id) {
              console.log('User ID changed, clearing all stores to prevent stale data');
              useJournalStore.getState().reset();
              useUserChannelsStore.getState().reset();
              useBoardStore.getState().clearBoardState();
            }

            set({
              user: {
                id: user.id,
                email: user.email,
                name: user.name || `${user.firstName} ${user.lastName}`,
                firstName: user.firstName,
                lastName: user.lastName,
                currency: user.currency,
                timezone: user.timezone,
                isActive: user.isActive,
                country: user.country,
                emailVerified: user.emailVerified ?? false,
                emailVerifiedAt: user.emailVerifiedAt,
                onboardingStatus: user.onboardingStatus,
                createdAt: user.createdAt
              },
              tokens: {
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
                expiresIn: 3600
              },
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
        } catch (error: any) {
          // Convert to translation keys
          let friendlyError = 'userErrors.auth.unableToSignIn';
          const status = error.response?.status;
          const serverError = error.response?.data?.error?.toLowerCase() || '';
          const errorCode = error.response?.data?.code;

          if (errorCode === 'AUTH_ACCOUNT_INACTIVE' || status === 403) {
            friendlyError = 'userErrors.auth.accountInactive';
          } else if (errorCode === 'AUTH_ACCOUNT_NOT_FOUND' || status === 404) {
            friendlyError = 'userErrors.auth.accountNotFound';
          } else if (errorCode === 'VALIDATION_ERROR' || status === 400 || serverError.includes('invalid email')) {
            friendlyError = 'userErrors.validation.message';
          } else if (errorCode === 'AUTH_INVALID_CREDENTIALS' || status === 401 || serverError.includes('invalid') || serverError.includes('credentials') || serverError.includes('password')) {
            friendlyError = 'userErrors.auth.invalidCredentials';
          } else if (errorCode === 'AUTH_TOKEN_EXPIRED' || errorCode === 'AUTH_TOKEN_REQUIRED' || errorCode === 'AUTH_INVALID_TOKEN') {
            friendlyError = 'userErrors.sessionExpired.message';
          } else if (status >= 500 || !error.response) {
            friendlyError = 'userErrors.serviceUnavailable.message';
          }

          set({
            isLoading: false,
            error: friendlyError
          })
          throw error
        }
      },

      register: async (userData: RegisterRequest) => {
        set({ isLoading: true, error: null })
        resetAuthInterceptorState()
        try {
          const response: AuthResponse = await authService.register(userData)
          if (response.success) {
            const { user, tokens } = response.data

            // Clear all stores on register - new user means fresh state
            // This also handles the edge case where localStorage had stale data
            const previousUserId = get().user?.id;
            if (previousUserId && previousUserId !== user.id) {
              console.log('User ID changed during register, clearing all stores');
              useJournalStore.getState().reset();
              useUserChannelsStore.getState().reset();
              useBoardStore.getState().clearBoardState();
            }

            set({
              user: {
                id: user.id,
                email: user.email,
                name: user.name || `${user.firstName} ${user.lastName}`,
                firstName: user.firstName,
                lastName: user.lastName,
                currency: user.currency,
                timezone: user.timezone,
                isActive: user.isActive,
                country: user.country,
                emailVerified: user.emailVerified ?? false,
                emailVerifiedAt: user.emailVerifiedAt,
                onboardingStatus: user.onboardingStatus,
                createdAt: user.createdAt
              },
              tokens: {
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
                expiresIn: 3600
              },
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
        } catch (error: any) {
          // Convert to translation keys
          let friendlyError = 'userErrors.auth.unableToRegister';
          const status = error.response?.status;
          const serverError = error.response?.data?.error?.toLowerCase() || '';
          const errorCode = error.response?.data?.code;

          if (errorCode === 'AUTH_EMAIL_ALREADY_EXISTS' || serverError.includes('already exists') || serverError.includes('already registered')) {
            friendlyError = 'userErrors.auth.emailAlreadyExists';
          } else if (errorCode === 'VALIDATION_ERROR' || status === 400 || serverError.includes('invalid email')) {
            friendlyError = 'userErrors.validation.message';
          } else if (status >= 500 || !error.response) {
            friendlyError = 'userErrors.serviceUnavailable.message';
          }

          set({
            isLoading: false,
            error: friendlyError
          })
          throw error
        }
      },

      socialLogin: async (provider: string, token: string) => {
        set({ isLoading: true, error: null })
        resetAuthInterceptorState()
        try {
          const response: AuthResponse = await authService.socialLogin(provider, token)
          if (response.success) {
            const { user, tokens } = response.data

            // User ID check similar to login/register actions
            const previousUserId = get().user?.id;
            if (previousUserId && previousUserId !== user.id) {
              console.log('User ID changed during social login, clearing all stores');
              useJournalStore.getState().reset();
              useUserChannelsStore.getState().reset();
              useBoardStore.getState().clearBoardState();
            }

            set({
              user: {
                id: user.id,
                email: user.email,
                name: user.name,
                firstName: user.firstName,
                lastName: user.lastName,
                currency: user.currency,
                timezone: user.timezone,
                isActive: user.isActive,
                country: user.country,
                emailVerified: user.emailVerified ?? true,
                emailVerifiedAt: user.emailVerifiedAt,
                onboardingStatus: user.onboardingStatus,
                createdAt: user.createdAt
              },
              tokens: {
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
                expiresIn: 3600
              },
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
        } catch (error: any) {
          const friendlyError = 'userErrors.auth.unableToSignIn';
          set({
            isLoading: false,
            error: friendlyError
          })
          throw error
        }
      },

      logout: async () => {
        // 1. Clear local state synchronously to stop any outgoing requests/loops immediately
        set({ 
          user: null, 
          tokens: null, 
          isAuthenticated: false,
          isLoading: false, 
          error: null 
        })

        // 2. Disconnect high-level services
        disconnectWebSocket();
        
        // 3. Clear all dependent stores
        try {
          useJournalStore.getState().reset();
          useUserChannelsStore.getState().reset();
          useBoardStore.getState().clearBoardState();
          useOnboardingStore.getState().clear();
        } catch (storeError) {
          console.warn('[AuthStore] Error clearing sub-stores:', storeError);
        }

        // 4. Attempt API logout (background)
        try {
          await authService.logout()
        } catch (error) {
          console.warn('[AuthStore] Remote logout failed, but local state is cleared')
        }
      },

      clearError: () => set({ error: null }),

      refreshTokens: async (): Promise<Tokens> => {
        const { tokens } = get();
        if (!tokens?.refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const response = await authService.refreshToken(tokens.refreshToken);
          const newTokens = {
            accessToken: response.data.tokens.accessToken,
            refreshToken: response.data.tokens.refreshToken,
            expiresIn: 3600
          };
          set({
            tokens: newTokens
          });
          return newTokens;
        } catch (error: any) {
          get().logout();
          throw error;
        }
      },

      setTokens: (tokens: Tokens) => set({ tokens }),
      setUser: (user: User) => set({ user }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user ? { ...state.user, onboardingStatus: undefined } : null,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
